import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect, useSelector } from 'react-redux'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'

import { skipToken } from '@reduxjs/toolkit/query'
import { cloneDeep, isEmpty } from 'lodash-es'
import queryString from 'query-string'
import styled from 'styled-components/native'

import { isAiConversationType } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import { AssignmentResponse } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { ItemAction } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/assignmentsList/AssignmentsList'
import { ExerciseResponsePage } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/exerciseResponsePage/ExerciseResponsePage'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'

import { track } from '../../../../../mixpanel/mixpanelTracking'
import { CLIENT_ACTIVITIES, LT_CLIENTS } from '../../../common/constants/routingConstants'
import { getAuthIsLTSupervisor, getAuthUserId } from '../../../data/auth/authSelectors'
import { getClientCurrentSessionCount, getSelectedClientId } from '../../../data/lyraTherapy/clientSelectors'
import { updateEndpointCache } from '../../../data/rtkQuery'
import { RootState, useAppDispatch } from '../../../data/store'
import { getProviderFullName } from '../../../providers/data/providerSelectors'
import { useSelectedClientData } from '../../clients/clientDetails/data/hooks/useSelectedClientData'
import ltClientApi from '../../clients/clientDetails/data/ltClientApi'
import { updateGetClientsCountsCache } from '../../clients/data/ltClientsApi'
import { setToastContent } from '../../data/ltToastAutoActions'
import {
  useGetPatientConversationsQuery,
  useLazyGetMessageQuery,
  useSendMessageMutation,
} from '../../messages/data/messagesApi'
import MiniBackNav from '../../miniBackNav/MiniBackNav'
import { useUpdateAssignmentResponseMutation } from '../data/assignmentsApi'
import { clearSelectedAssignmentId, setSelectedAssignmentId } from '../data/assignmentsSlice'
import { useSelectedAssignment } from '../data/hooks/useSelectedAssignment'

type AssignmentDetailsProps = {
  currentSessionCount?: number
  isLtSupervisor: boolean
  userId?: string
}

const AssignmentDetailsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  maxWidth: theme.breakpoints.isMinWidthDesktop ? '1140px' : '970px',
  margin: '30px auto',
  padding: theme.breakpoints.isMinWidthDesktop ? '0px' : '0px 15px',
}))

const ResponsePageContainer = styled.View(() => ({
  marginTop: '10px',
  flexShrink: 1,
  flexGrow: 1,
}))

const AssignmentDetails: FunctionComponent<AssignmentDetailsProps> = ({
  currentSessionCount,
  isLtSupervisor,
  userId,
}) => {
  const { formatMessage } = useIntl()
  const { search, state } = useLocation()
  const { activityActions } = useOutletContext<{ activityActions: ItemAction[] }>()
  const [getMessage] = useLazyGetMessageQuery()
  const [sendMessage] = useSendMessageMutation()
  const [updateAssignmentResponse] = useUpdateAssignmentResponseMutation()
  const selectedClientId = useSelector(getSelectedClientId)
  const { data: patientConversations } = useGetPatientConversationsQuery(
    selectedClientId && userId
      ? { providerId: userId, patientId: selectedClientId, types: CONVERSATION_TYPE.PROVIDER }
      : skipToken,
  )
  const patientConversationId = useMemo(
    () =>
      patientConversations?.find(
        (conversation) =>
          conversation.patient_lyra_id === selectedClientId &&
          !isAiConversationType(conversation.conversation_attributes?.type),
      )?.conversation_id ?? null,
    [patientConversations, selectedClientId],
  )
  const navigate = useNavigate()
  const { data: clientDetails, isLoading: isLoadingClientDetails } = useSelectedClientData()
  const dispatch = useAppDispatch()
  const selectedAssignment = useSelectedAssignment()

  const sessionCount: number = state?.sessionCount ?? currentSessionCount

  const navigateBackToAssignments = useCallback(() => {
    const route = CLIENT_ACTIVITIES.route
    navigate(route, { state: { sessionCount: sessionCount } })
  }, [navigate, sessionCount])

  const onResponseViewed = useCallback(
    (response: AssignmentResponse) => {
      if (isLtSupervisor || response.viewed) {
        return
      }
      updateAssignmentResponse({ ...response, viewed: true })
        .unwrap()
        .then(() => {
          updateGetClientsCountsCache(dispatch, userId, clientDetails)
          dispatch(
            updateEndpointCache(ltClientApi, 'getClient')
              .where({
                providerId: userId,
                clientId: clientDetails?.id,
              })
              .build((client) => {
                client.unread_responses = client.unread_responses - 1
              }),
          )
        })
    },
    [clientDetails, dispatch, isLtSupervisor, updateAssignmentResponse, userId],
  )

  const fetchComment = useCallback(
    ({ messageId }: { messageId: string }) => {
      if (!patientConversationId || !userId) {
        return Promise.reject(new Error('No patient conversation available'))
      }
      return getMessage({
        conversationId: patientConversationId,
        providerId: userId,
        messageId,
        append: false,
      }).unwrap()
    },
    [getMessage, patientConversationId, userId],
  )

  useEffect(() => {
    if (selectedClientId && !isLoadingClientDetails && !clientDetails) {
      navigate(LT_CLIENTS.route)
    }
  }, [selectedClientId, clientDetails, isLoadingClientDetails, navigate])

  useEffect(() => {
    return () => {
      dispatch(clearSelectedAssignmentId())
    }
  }, [dispatch])

  useEffect(() => {
    const assignmentId = state?.assignmentId || queryString.parse(search).assignmentId
    if (!selectedAssignment) {
      if (assignmentId) {
        dispatch(setSelectedAssignmentId(assignmentId as string))
      } else {
        navigateBackToAssignments()
      }
    }
  }, [dispatch, navigateBackToAssignments, search, selectedAssignment, state])

  const handleSubmitComment = async (inputValue: string, response: AssignmentResponse) => {
    if (!patientConversationId || !userId) {
      dispatch(
        setToastContent({
          text: formatMessage({
            defaultMessage: "Something went wrong and your comment wasn't added.",
            description: 'Toast error shown when submitting a provider comment on an assignment fails',
          }),
          toastType: 'error',
          id: 'AssignmentDetails-submitComment-error',
        }),
      )
      return undefined
    }
    const data = {
      message_body: inputValue,
      message_type: 'feedback',
      message_author_id: userId,
      message_author_type: 'provider',
      metadata: {
        activity_id: response.assignment_id,
        activity_response_id: response.id,
        title: selectedAssignment?.content.title,
        linkText: 'View Entry',
        submit_date: response.submit_date,
      },
    }

    try {
      const message = await sendMessage({
        conversationId: patientConversationId,
        data: {
          ...data,
        },
      }).unwrap()
      return await updateAssignmentResponse({
        ...response,
        provider_feedback: message.message_id,
        viewed: true,
      }).unwrap()
    } catch {
      dispatch(
        setToastContent({
          text: formatMessage({
            defaultMessage: "Your comment couldn't be added. Please try again.",
            description: 'Toast error shown when submitting a provider comment on an assignment fails',
          }),
          toastType: 'error',
          id: 'AssignmentDetails-submitComment-apiError',
        }),
      )
      return undefined
    }
  }

  return !selectedAssignment || isEmpty(selectedAssignment) || !currentSessionCount ? (
    <LoadingIndicator size={45} />
  ) : (
    <AssignmentDetailsContainer>
      <MiniBackNav backFunc={navigateBackToAssignments} subText={`Activities for Session ${sessionCount}`} />
      <ResponsePageContainer>
        <ExerciseResponsePage
          content={cloneDeep({ ...(selectedAssignment as any), meta_data: selectedAssignment?.content_meta_data })}
          data={(selectedAssignment?.assignment_responses ?? []) as AssignmentResponse[] & never[]}
          fetchComment={fetchComment}
          itemActions={activityActions.filter((action: ItemAction) =>
            action.shouldShow(selectedAssignment, sessionCount),
          )}
          onResponseViewed={onResponseViewed}
          receiverName={clientDetails?.first_name ?? ''}
          responseIdFromNavigation={state?.responseId}
          sessionCount={currentSessionCount}
          submitComment={handleSubmitComment}
          track={track}
        />
      </ResponsePageContainer>
    </AssignmentDetailsContainer>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    providerFullname: getProviderFullName(state),
    userId: getAuthUserId(state),
    currentSessionCount: getClientCurrentSessionCount(state),
    isLtSupervisor: getAuthIsLTSupervisor(state),
  }
}

export default connect(mapStateToProps, null)(AssignmentDetails)
